import React from "react";

interface LogoProps {
  /** Width in pixels (height scales proportionally). Default 36. */
  size?: number;
  className?: string;
}

/**
 * Qpicks.io brand mark — a stylised "Q" set inside a diamond shape,
 * evoking precision, analytics, and sports. Uses the brand emerald
 * gradient so it pairs with the site-wide accent palette.
 */
export function Logo({ size = 36, className }: LogoProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* Cyan gradient — matches --accent palette */}
        <linearGradient id="qp-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3dd68c" />
          <stop offset="100%" stopColor="#00c07a" />
        </linearGradient>
        {/* Subtle glow behind the mark */}
        <filter id="qp-glow">
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Diamond backdrop */}
      <rect
        x="5"
        y="5"
        width="38"
        height="38"
        rx="10"
        fill="url(#qp-grad)"
        opacity="0.12"
        transform="rotate(0 24 24)"
      />
      <rect
        x="5"
        y="5"
        width="38"
        height="38"
        rx="10"
        fill="none"
        stroke="url(#qp-grad)"
        strokeWidth="1.5"
        opacity="0.35"
      />

      {/* Stylised "Q" letterform */}
      <g filter="url(#qp-glow)">
        {/* Q circle */}
        <circle
          cx="23"
          cy="22"
          r="10"
          fill="none"
          stroke="url(#qp-grad)"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
        {/* Q tail — angled slash */}
        <line
          x1="29"
          y1="27"
          x2="36"
          y2="35"
          stroke="url(#qp-grad)"
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      </g>

      {/* Data-dot accent — suggests analytics precision */}
      <circle cx="36" cy="12" r="2.2" fill="#00c07a" opacity="0.85" />
    </svg>
  );
}
